import clsx from "clsx";
import styles from "./Tabs.module.scss";

export interface TabDef<K extends string> {
  key: K;
  label: string;
}

export interface TabsProps<K extends string> {
  tabs: readonly TabDef<K>[];
  active: K;
  onChange: (key: K) => void;
  /** Names the tablist for assistive tech, e.g. "Settings sections". */
  label: string;
  className?: string;
}

/**
 * The panel-swapping tab strip: mono uppercase labels on a hairline that spans its container, the active
 * one underlined in crimson.
 *
 * **This is for tabs that swap PANELS.** The pill-in-a-track is a different control — a mode/scope toggle
 * that narrows one view (see COMPONENTS) — and the two are not interchangeable; Settings used the pill as a
 * tab strip until the spec corrected it (2026-08-17).
 *
 * Roving focus, not one stop per tab: arrows move between tabs and only the active tab is tabbable, which is
 * the WAI-ARIA tabs pattern. Activation follows focus, so arrowing swaps the panel — matching what a mouse
 * click does, with no second keypress to "confirm".
 */
export function Tabs<K extends string>({ tabs, active, onChange, label, className }: TabsProps<K>) {
  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    const i = tabs.findIndex((t) => t.key === active);
    const to =
      e.key === "ArrowLeft" ? i - 1
      : e.key === "ArrowRight" ? i + 1
      : e.key === "Home" ? 0
      : e.key === "End" ? tabs.length - 1
      : null;
    if (to === null || i < 0) return;
    const next = tabs[(to + tabs.length) % tabs.length];
    if (!next) return;
    e.preventDefault(); // arrows would otherwise scroll the panel
    onChange(next.key);
  }

  return (
    <div className={clsx(styles.strip, className)} role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          tabIndex={active === t.key ? 0 : -1}
          className={styles.tab}
          data-active={active === t.key || undefined}
          onClick={() => onChange(t.key)}
          onKeyDown={onKeyDown}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
